/**
 * A glass jar of marbles, for families who count in marbles.
 *
 * The count is the point, so every marble is a real one up to what the jar
 * holds (about fifty). Past that, each marble drawn stands for a round number
 * of them -- 2, 5, 10, 20... -- and the jar says so, rather than cramming in
 * marbles too small to see from across a kitchen.
 *
 * THE ANIMATION IS THE FEATURE. When marbles are earned they drop in through
 * the mouth of the jar one after another, bounce, and settle -- one marble per
 * marble, so a chore worth three is three little drops a child can count.
 * Spending lifts marbles out the same way. `from` says what the jar held when
 * it was last seen, so a child opening their page watches what they earned
 * since yesterday fall in; after that, any change while the jar is on screen
 * animates by itself. `prefers-reduced-motion` gets the settled jar.
 *
 * Pure SVG and CSS -- no image files, nothing to load -- and every marble's
 * place and colour is a function of its index, so the same count always draws
 * the same jar.
 */

import { useEffect, useId, useMemo, useState } from 'react';
import { currentUnit } from '../family/money';

/** Marbles the jar holds before each drawn marble stands for several. */
const CAPACITY = 50;
const STEPS = [1, 2, 5, 10, 20, 50, 100, 200, 500, 1000];
const COLOURS = ['#3f7fd8', '#d84b45', '#3aa66b', '#efbd2c', '#8b5ad6', '#ee8a2d', '#27aeb0', '#e46aa6'];
const DROP_MS = 140; // between one marble and the next
const SETTLE_MS = 900; // one marble's fall and bounce

// The inside of the jar, in viewBox units.
const LEFT = 21;
const RIGHT = 99;
const BOTTOM = 137;
const R = 5.6;

/** The slot marble `i` rests in: rows from the bottom, alternate rows offset. */
function slot(i: number): { x: number; y: number } {
  const width = RIGHT - LEFT;
  let row = 0;
  let index = i;
  for (;;) {
    const offset = row % 2 ? R : 0;
    const perRow = Math.floor((width - offset) / (2 * R));
    if (index < perRow) {
      // A touch of deterministic jitter, so it looks poured and not stacked.
      const jitter = (((i * 37) % 7) - 3) * 0.18;
      return {
        x: LEFT + offset + R + index * 2 * R + jitter,
        y: BOTTOM - R - row * 2 * R * 0.87 + Math.abs(jitter) * 0.4,
      };
    }
    index -= perRow;
    row += 1;
  }
}

/** How many marbles each drawn marble stands for, so the jar never overflows. */
function each(count: number): number {
  return STEPS.find((step) => Math.ceil(count / step) <= CAPACITY) ?? STEPS[STEPS.length - 1]!;
}

export function MarbleJar({
  count,
  from,
  size = 120,
  label,
}: {
  /** Whole marbles. A part-marble left over from before a switch is dropped. */
  count: number;
  /** What the jar held when last seen, to animate the difference on arrival. */
  from?: number;
  size?: number;
  /** Read out instead of the default "A jar with 12 marbles". */
  label?: string;
}) {
  const whole = Math.max(0, Math.floor(count));
  const [settled, setSettled] = useState(() => Math.max(0, Math.floor(from ?? whole)));
  const id = useId().replace(/:/g, '');

  // Once the drops have had time to land, what was falling is simply there.
  useEffect(() => {
    if (whole === settled) return;
    const moving = Math.abs(whole - settled);
    const unit = each(Math.max(whole, settled));
    const drawn = Math.min(Math.ceil(moving / unit), CAPACITY);
    const timer = window.setTimeout(() => setSettled(whole), drawn * DROP_MS + SETTLE_MS);
    return () => window.clearTimeout(timer);
  }, [whole, settled]);

  const unit = each(Math.max(whole, settled));
  const drawnNow = Math.ceil(whole / unit);
  const drawnBefore = Math.ceil(settled / unit);
  const drawn = Math.max(drawnNow, drawnBefore);

  const marbles = useMemo(
    () =>
      Array.from({ length: drawn }, (_, i) => {
        const { x, y } = slot(i);
        const state = i < Math.min(drawnNow, drawnBefore) ? 'still' : i < drawnNow ? 'falling' : 'leaving';
        const order = state === 'falling' ? i - drawnBefore : state === 'leaving' ? drawnBefore - 1 - i : 0;
        return { i, x, y, state, order, colour: (i * 5) % COLOURS.length };
      }),
    [drawn, drawnNow, drawnBefore],
  );

  return (
    <svg
      className="marble-jar"
      viewBox="0 0 120 150"
      width={size}
      height={(size * 150) / 120}
      role="img"
      aria-label={label ?? `A jar with ${whole} ${whole === 1 ? 'marble' : 'marbles'}`}
    >
      <defs>
        {COLOURS.map((colour, c) => (
          <radialGradient key={c} id={`${id}m${c}`} cx="0.36" cy="0.32" r="0.75">
            <stop offset="0" stopColor="#ffffff" stopOpacity="0.95" />
            <stop offset="0.18" stopColor={colour} stopOpacity="0.9" />
            <stop offset="0.85" stopColor={colour} />
            <stop offset="1" stopColor="#1b1b28" stopOpacity="0.55" />
          </radialGradient>
        ))}
        <linearGradient id={`${id}glass`} x1="0" x2="1">
          <stop offset="0" stopColor="#ffffff" stopOpacity="0.5" />
          <stop offset="0.25" stopColor="#ffffff" stopOpacity="0.12" />
          <stop offset="0.8" stopColor="#ffffff" stopOpacity="0.08" />
          <stop offset="1" stopColor="#ffffff" stopOpacity="0.35" />
        </linearGradient>
        <clipPath id={`${id}inside`}>
          <path d="M40 30 C 40 36, 16 38, 16 52 L 16 128 Q 16 142, 30 142 L 90 142 Q 104 142, 104 128 L 104 52 C 104 38, 80 36, 80 30 Z" />
        </clipPath>
      </defs>

      {/* The back of the glass, faintly tinted. */}
      <path
        d="M40 30 C 40 36, 16 38, 16 52 L 16 128 Q 16 142, 30 142 L 90 142 Q 104 142, 104 128 L 104 52 C 104 38, 80 36, 80 30 Z"
        fill="#dfeef0"
        fillOpacity="0.28"
      />

      <g clipPath={`url(#${id}inside)`}>
        {marbles.map((m) => (
          <g
            key={m.i}
            className={`marble ${m.state}`}
            style={
              m.state === 'still'
                ? undefined
                : ({
                    // From the mouth of the jar to the marble's own place.
                    ['--dx' as string]: `${(60 - m.x).toFixed(1)}px`,
                    ['--dy' as string]: `${(12 - m.y).toFixed(1)}px`,
                    animationDelay: `${m.order * DROP_MS}ms`,
                  } as React.CSSProperties)
            }
          >
            <circle cx={m.x} cy={m.y} r={R} fill={`url(#${id}m${m.colour})`} />
            <ellipse cx={m.x - R * 0.35} cy={m.y - R * 0.4} rx={R * 0.28} ry={R * 0.18} fill="#fff" fillOpacity="0.8" />
          </g>
        ))}
      </g>

      {/* The front of the glass: its outline and the light on it. */}
      <path
        d="M40 30 C 40 36, 16 38, 16 52 L 16 128 Q 16 142, 30 142 L 90 142 Q 104 142, 104 128 L 104 52 C 104 38, 80 36, 80 30 Z"
        fill={`url(#${id}glass)`}
        stroke="#a9c3c8"
        strokeWidth="2"
      />
      <path d="M24 58 Q 22 90, 25 122" stroke="#ffffff" strokeOpacity="0.7" strokeWidth="3.2" strokeLinecap="round" fill="none" />
      {/* The lid. */}
      <rect x="36" y="14" width="48" height="16" rx="4" fill="#b8844f" stroke="#8a5f35" strokeWidth="1.5" />
      <rect x="36" y="18" width="48" height="3" fill="#d9a870" fillOpacity="0.7" />

      {unit > 1 ? (
        <text x="60" y="148" textAnchor="middle" fontSize="8" fill="currentColor" opacity="0.75">
          each marble = {unit}
        </text>
      ) : null}
    </svg>
  );
}

/** What a jar last showed, per jar, on this device: so opening it animates what is new. */
export function lastSeen(key: string): number | undefined {
  try {
    const value = localStorage.getItem(`valley.jar.${key}`);
    return value === null ? undefined : Number(value);
  } catch {
    return undefined;
  }
}

export function noteSeen(key: string, count: number): void {
  try {
    localStorage.setItem(`valley.jar.${key}`, String(count));
  } catch {
    /* private window: the next visit just does not animate */
  }
}

/**
 * A jar that remembers, on this device, what it last showed -- so each visit
 * drops in exactly what was earned since the last one. Draws nothing unless
 * the family counts in marbles, so it can be placed unconditionally.
 */
export function RememberedJar({ id, hundredths, size }: { id: string; hundredths: number; size?: number }) {
  const count = Math.max(0, Math.floor(hundredths / 100));
  const [from] = useState(() => lastSeen(id));
  useEffect(() => noteSeen(id, count), [id, count]);
  if (currentUnit().type !== 'marbles') return null;
  return <MarbleJar count={count} {...(from !== undefined ? { from } : {})} {...(size ? { size } : {})} />;
}
